import React from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';

import {
  CodeToPath,
  FULL_WIZARD_STEPS,
  StepCode,
  StepCodes,
  WIZARD_STEPS_PATH,
  WizardStep,
} from './steps';
import { useHasTrackingCode } from '../../hooks/usePrerenderUser';
// The leaf, not the feature's barrel: `cloudflare-integration/index.ts` pulls in
// the screens, which this wizard's own steps render — importing it here would
// close the loop.
import { isGuidesView } from '../cloudflare-integration/integrationGuidesRoute';

const EMAIL_DEEP_LINK_MARKER = 'src';

function hasEmailDeepLinkMarker(searchParams: URLSearchParams): boolean {
  return searchParams.get(EMAIL_DEEP_LINK_MARKER) === 'email';
}

function withoutEmailDeepLinkMarker(searchParams: URLSearchParams): string {
  const remaining = new URLSearchParams(searchParams);
  remaining.delete(EMAIL_DEEP_LINK_MARKER);
  const query = remaining.toString();

  return query ? `?${query}` : '';
}

function saveLocalProgress(userId: number, stepCode: StepCode) {
  localStorage.setItem(`integrationWizardStep_${userId}`, stepCode);
}

function getLocalProgress(userId: number): StepCode | void {
  const localStorageCurrentStep = localStorage.getItem(`integrationWizardStep_${userId}`);
  if (localStorageCurrentStep) {
    return localStorageCurrentStep as StepCode;
  }
}

function advanceLocalProgressTo(userId: number, stepCode: StepCode, stepsConfig: WizardStep[]) {
  const storedStepIndex = stepsConfig.findIndex((step) => step.code === getLocalProgress(userId));
  const targetStepIndex = stepsConfig.findIndex((step) => step.code === stepCode);

  if (storedStepIndex < targetStepIndex) saveLocalProgress(userId, stepCode);
}

function useWizardSteps(userId: number, surveyCompleted: boolean) {
  const stepsConfigCopy = React.useRef([...FULL_WIZARD_STEPS]);
  const stepsConfig = stepsConfigCopy.current;

  const navigate = useNavigate();
  const location = useLocation();
  const hasIntegrated = useHasTrackingCode();
  const [searchParams] = useSearchParams();
  const deepLinkedToIntegrate = hasEmailDeepLinkMarker(searchParams);
  // Held for the life of the mount, not in state: the redirect below consumes the marker, so by
  // the next render nothing in the URL still says this visit came from an email link, and the
  // stored-progress branch would bounce the customer straight off the step the link opened.
  const pinnedToEmailDeepLink = React.useRef(false);

  // The route is the single source of truth for the current step. Holding the index in state
  // instead let a mover commit a render where the index had already moved but the pathname had
  // not — react-router v7 navigates inside `startTransition`, so the index update lands first.
  // The effect below then read an index that disagreed with the route and issued its own
  // corrective `navigate()` with no location state, discarding the state the mover passed and
  // leaving the Verify step's "Continue with Nexus" unable to open the chat (PRE-3482).
  // Deriving it also keeps the progress bar in step with the route it is describing.
  const currentStepRoute = location.pathname.split('/').slice(3)[0];
  const routeStepIndex = stepsConfig.findIndex((step) => step.path === currentStepRoute);
  const currentStepIndex = routeStepIndex === -1 ? 0 : routeStepIndex;

  React.useEffect(() => {
    if (hasIntegrated) {
      navigate('/integration-wizard/setup-checklist');
      return;
    }

    if (deepLinkedToIntegrate) {
      pinnedToEmailDeepLink.current = true;
      navigate(
        `${WIZARD_STEPS_PATH}/${CodeToPath[StepCodes.Integrate]}${withoutEmailDeepLinkMarker(searchParams)}`,
        { replace: true }
      );
      advanceLocalProgressTo(userId, StepCodes.Integrate, stepsConfig);
      return;
    }

    // The guides are a view of this step, so a customer reading them is where
    // they asked to be — stored progress must not pull them off it on a reload.
    if (isGuidesView(location.search) && currentStepRoute === CodeToPath[StepCodes.Integrate]) return;

    if (pinnedToEmailDeepLink.current) {
      if (currentStepRoute === CodeToPath[StepCodes.Integrate]) return;
      pinnedToEmailDeepLink.current = false;
    }

    const storedStepCode = getLocalProgress(userId);
    const storedStepIndex = stepsConfig.findIndex((step) => step.code === storedStepCode);

    if (storedStepIndex === -1) {
      const firstStepLocation = stepsConfig[surveyCompleted ? 1 : 0].path;
      if (currentStepRoute !== firstStepLocation) navigate(`${WIZARD_STEPS_PATH}/${firstStepLocation}`);
      return;
    }

    if (currentStepRoute !== stepsConfig[storedStepIndex].path) navigate(`${WIZARD_STEPS_PATH}/${stepsConfig[storedStepIndex].path}`);
  }, [userId, currentStepRoute, location.search, surveyCompleted, hasIntegrated, deepLinkedToIntegrate]);

  const goNext = React.useCallback(
    (steps = 1) => {
      if (currentStepIndex < stepsConfig.length - steps) {
        saveLocalProgress(userId, stepsConfig[currentStepIndex + steps].code);
        navigate(`${WIZARD_STEPS_PATH}/${stepsConfig[currentStepIndex + steps].path}`);
      }
    },
    [currentStepIndex, stepsConfig, userId]
  );

  const goBack = React.useCallback(() => {
    if (currentStepIndex > 0) {
      saveLocalProgress(userId, stepsConfig[currentStepIndex - 1].code);
      navigate(`${WIZARD_STEPS_PATH}/${stepsConfig[currentStepIndex - 1].path}`);
    }
  }, [currentStepIndex, stepsConfig, userId]);

  const jumpToStep = React.useCallback((stepCode: StepCode, state?: Record<string, unknown>) => {
    const stepIndex = stepsConfig.findIndex((step) => step.code === stepCode);
    if (stepIndex !== -1) {
      saveLocalProgress(userId, stepCode);
      navigate(`${WIZARD_STEPS_PATH}/${stepsConfig[stepIndex].path}`, { state });
    }
  }, [stepsConfig, userId]);

  return { stepsConfig, currentStepIndex, goNext, goBack, jumpToStep };
}

export default useWizardSteps;
